'use client';

import Input from 'src/components/primitives/Input/Input';
import { useEffect, useMemo, useState } from 'react';
import useAccountData from 'src/hooks/useAccountData';
import { DEFAULT_PUBLIC_KEY } from 'src/@types/types';
import { PublicKey } from '@drift-labs/sdk';
import useDriftStore from 'src/stores/DriftStore/useDriftStore';
import { Connection } from '@solana/web3.js';
import useModal from 'src/components/Modals/hooks/useModal';
import ResponsiveModal from 'src/components/Modals/ResponsiveModal/ResponsiveModal';
import Link from 'src/components/primitives/Link/Link';
import Button from 'src/components/primitives/Button/Button';
import InlineAlert from '../primitives/InlineAlert/InlineAlert';
import { usePrivyEmbeddedWalletWithFallback } from 'src/hooks/wallets/privy/mappings/usePrivyEmbeddedWalletWithFallback';
import { useUpdateSubaccountDelegate } from 'src/hooks/driftActions/user/useUpdateSubaccountDelegate';

export interface DelegateModalProps {
	delegateAuthority: PublicKey;
}

const DelegateModal = ({ delegateAuthority }: DelegateModalProps) => {
	const connection = useDriftStore((s) => s.connection?.current) as Connection;
	const { updateSubaccountDelegate } = useUpdateSubaccountDelegate();
	const currentAccount = useAccountData();

	const { hide } = useModal();

	const embeddedWallet = usePrivyEmbeddedWalletWithFallback();

	const selectedSubAccountId = currentAccount?.userId;
	const selectedSubAccountName = currentAccount?.name;
	const isDelegated = !delegateAuthority.equals(DEFAULT_PUBLIC_KEY);
	const isDelegatedToEmbeddedWallet = useMemo(
		() => !!embeddedWallet && delegateAuthority.equals(embeddedWallet),
		[embeddedWallet, delegateAuthority]
	);
	const isDelegatedToNonEmbeddedWallet =
		isDelegated && !isDelegatedToEmbeddedWallet;

	const [delegateToAddress, setDelegateToAddress] = useState(
		isDelegatedToNonEmbeddedWallet ? delegateAuthority.toString() : ''
	);

	const [errorMessage, setErrorMessage] = useState('');
	const [warningMessage, setWarningMessage] = useState('');

	const [isRemovingDelegate, setIsRemovingDelegate] = useState(false);
	const [isSubmitting, setIsSubmitting] = useState(false);

	const isSubmitDisabled = errorMessage !== '' || delegateToAddress === '';

	const checkValidAddress = (inputAddress: string) => {
		if (!inputAddress) return false;
		try {
			return PublicKey.isOnCurve(inputAddress);
		} catch (_e) {
			return false;
		}
	};

	const checkNotSameAuthority = (inputAddress: string) => {
		return inputAddress !== currentAccount?.authority.toString();
	};

	const checkWalletHasSol = async (inputAddress: string) => {
		const accountInfo = await connection.getAccountInfo(
			new PublicKey(inputAddress)
		);

		if (!accountInfo || !accountInfo.lamports || accountInfo.lamports === 0) {
			return false;
		}
		return true;
	};

	const handleDelegateAccount = async () => {
		if (!checkValidAddress(delegateToAddress)) {
			setErrorMessage('Invalid address');
			return;
		}

		if (!checkNotSameAuthority(delegateToAddress)) {
			setErrorMessage('Cannot delegate to same authority');
			return;
		}

		// stop from continuing the first time, if they make no changes and click it again, let it happen
		if (!warningMessage) {
			if (!(await checkWalletHasSol(delegateToAddress))) {
				setWarningMessage(
					'Address has no SOL. Please verify before continuing.'
				);
				return;
			}
		}

		setIsSubmitting(true);

		const result = await updateSubaccountDelegate(
			selectedSubAccountId ?? 0,
			delegateToAddress
		);

		if (result === true) {
			hide();
		}

		setIsSubmitting(false);
	};

	const removeDelegateAccount = async () => {
		if (!isDelegated || isDelegatedToEmbeddedWallet) {
			hide();
			return;
		}

		setIsRemovingDelegate(true);

		// we delegate to the embedded wallet if it exists, otherwise remove the delegation completely
		const result = await updateSubaccountDelegate(
			selectedSubAccountId ?? 0,
			embeddedWallet ? embeddedWallet.toString() : DEFAULT_PUBLIC_KEY.toString()
		);

		if (result === true) {
			hide();
		}

		setIsRemovingDelegate(false);
	};

	useEffect(() => {
		// remove error message if they edit the box after an invalid entry
		if (errorMessage !== '') {
			setErrorMessage('');
		}

		if (warningMessage !== '') {
			setWarningMessage('');
		}
	}, [delegateToAddress]);

	useEffect(() => {
		if (!isDelegatedToNonEmbeddedWallet) {
			setDelegateToAddress('');
		}
	}, [isDelegatedToNonEmbeddedWallet]);

	if (!currentAccount) {
		return null;
	}

	return (
		<ResponsiveModal.Container
			onClose={hide}
			className="sm:min-w-[480px]"
			dataTestId="delegate-modal"
		>
			<ResponsiveModal.Title closeButtonTestId="delegate-modal-close">
				Delegate Your Account
			</ResponsiveModal.Title>
			<ResponsiveModal.Body>
				<p className="typo-b3 text-text-default">
					Delegate your subaccount ({selectedSubAccountName}) to another wallet
					address. Delegates can only deposit, swap, place orders, and cancel
					orders. They cannot withdraw funds from your account. <br />
					<Link
						href="https://docs.drift.trade/getting-started/delegated-accounts"
						openInNewTab
					>
						Learn more about Delegate Permissions
					</Link>
				</p>

				{isDelegatedToEmbeddedWallet && (
					<InlineAlert
						variant="warning"
						title="Auto-Signing will be disabled for this account once you delegate"
					/>
				)}

				<Input
					id="delegate-address"
					labelVariant="standard"
					label="Wallet Address"
					type="text"
					value={delegateToAddress}
					onChange={setDelegateToAddress}
				/>

				{errorMessage && (
					<InlineAlert variant="error" title="Error">
						{errorMessage}
					</InlineAlert>
				)}

				{warningMessage && (
					<InlineAlert variant="warning" title="Warning">
						{warningMessage}
					</InlineAlert>
				)}
			</ResponsiveModal.Body>

			<ResponsiveModal.Footer>
				<Button
					size="lg"
					variant="secondary"
					className="flex-1"
					onClick={removeDelegateAccount}
					isLoading={isRemovingDelegate}
					dataTestId="delegate-modal-cancel"
				>
					{isDelegatedToNonEmbeddedWallet ? 'Remove' : 'Cancel'}
				</Button>

				<Button
					size="lg"
					className="flex-1"
					disabled={isSubmitDisabled}
					onClick={handleDelegateAccount}
					isLoading={isSubmitting}
					dataTestId="delegate-modal-submit"
				>
					{isDelegatedToNonEmbeddedWallet ? 'Update' : 'Confirm'}
				</Button>
			</ResponsiveModal.Footer>
		</ResponsiveModal.Container>
	);
};

export default DelegateModal;
